import {
    PagebuilderFooterPropertiesModel
} from "@de-trustleap/shared-models";
import { BaseWidget } from "./baseWidget";
import {
    getPrivacyPolicy,
    getImpressum,
    getInitialInfo,
    getTermsAndConditions,
} from "../utils/getLegalDocument";
import { buildLegalLink } from "../utils/legalLinkUtils";
import { Logger } from "../utils/logger";

export class FooterWidget extends BaseWidget<PagebuilderFooterPropertiesModel> {
    constructor(private widget: any) {
        super({
            ...widget.properties,
            background: widget.background,
            shadow: widget.shadow
        });
    }

    public async render(): Promise<HTMLElement> {
        const footer = document.createElement("footer");

        this.applyStyles(footer, {
            width: "100%",
            boxSizing: "border-box",
            color: "#fff",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "10px"
        });

        const linkContainer = await this.createLinkContainer();
        const logo = this.createLogo();

        footer.appendChild(linkContainer);
        footer.appendChild(logo);

        return footer;
    }

    private async createLinkContainer(): Promise<HTMLElement> {
        const container = document.createElement("div");

        this.applyStyles(container, {
            display: "flex",
            gap: "8px",
            justifyContent: "center",
            flexWrap: "wrap"
        });

        const [privacyText, impressumText, initialInfoText, termsText] = await Promise.all([
            getPrivacyPolicy(),
            getImpressum(),
            getInitialInfo(),
            getTermsAndConditions()
        ]);

        const links: HTMLElement[] = [];

        if (privacyText?.trim()) {
            links.push(this.createLink(
                "Datenschutzerklärung",
                buildLegalLink("/legalpages/datenschutz.html"),
                this.properties.privacyPolicyTextProperties
            ));
        }

        if (impressumText?.trim()) {
            links.push(this.createLink(
                "Impressum",
                buildLegalLink("/legalpages/impressum.html"),
                this.properties.impressumTextProperties
            ));
        }

        if (initialInfoText?.trim()) {
            links.push(this.createLink(
                "Erstinformation",
                buildLegalLink("/legalpages/erstinformation.html"),
                this.properties.initialInformationTextProperties
            ));
        }

        if (termsText?.trim()) {
            links.push(this.createLink(
                "AGB",
                buildLegalLink("/legalpages/agb.html"),
                this.properties.termsAndConditionsTextProperties
            ));
        }


        links.forEach((link, index) => {
            container.appendChild(link);
            if (index < links.length - 1) {
                const separator = document.createElement("span");
                separator.textContent = "|";
                separator.style.color = "#fff";
                separator.style.margin = "0 8px";
                container.appendChild(separator);
            }
        });

        Logger.info("📎 Footer verwendet LandingPage-ID:", buildLegalLink(""));

        return container;
    }

    private createLink(text: string, url: string, textProps?: any): HTMLElement {
        const link = document.createElement("a");
        link.href = url;
        link.textContent = text;
        link.target = "_blank";
        link.rel = "noopener noreferrer";

        const color = this.formatColor(textProps?.color, "#ffffff");

        this.applyStyles(link, {
            color,
            fontSize: `${textProps?.fontSize ?? 16}px`,
            fontFamily: textProps?.fontFamily ?? "Arial, sans-serif",
            textDecoration: "none",
            transition: "color 0.2s ease-in-out, text-decoration 0.2s ease-in-out"
        });

        link.addEventListener("mouseover", () => {
            this.applyStyles(link, {
                textDecoration: "underline",
                color: "#E1E1E1"
            });
        });

        link.addEventListener("mouseout", () => {
            this.applyStyles(link, {
                textDecoration: "none",
                color
            });
        });

        return link;
    }

    private createLogo(): HTMLImageElement {
        const logo = document.createElement("img");
        logo.src = "./imgs/Logo web-03.svg";
        logo.alt = "Logo";

        this.applyStyles(logo, {
            width: "100px",
            height: "auto",
            borderRadius: "5px",
            transition: "opacity 0.3s ease-in-out"
        });

        logo.onerror = () => logo.remove();
        return logo;
    }
}
